import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { useAuth } from "@/hooks/useAuth";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import Logo from '@/components/Logo';
import Layout from '@/components/layout/Layout';

const Register = () => {
    const navigate = useNavigate();
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const { register } = useAuth();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            await register(email.trim(), password.trim(), name.trim(), '');
            toast.success('Registration successful! Please login.');
            navigate('/login');
        } catch (error: unknown) {
            toast.error(error instanceof Error ? error.message : 'Registration failed');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Layout>
            <div className="flex items-center justify-center bg-background px-4 relative overflow-hidden py-10">
                <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-accent/5 blur-[120px] z-0" />
                <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-primary/5 blur-[120px] z-0" />

                <div className="w-full max-w-md relative z-10">
                    <div className="card-premium !p-10 bg-white/40 border-white/60">
                        <div className="flex flex-col items-center mb-10 text-center">
                            <div className="w-20 h-20 rounded-[2rem] bg-white shadow-premium flex items-center justify-center mb-6 border border-white/60 group">
                                <Logo showText={false} size={48} className="transition-transform duration-500 group-hover:scale-110" />
                            </div>
                            <h1 className="text-3xl font-black text-primary uppercase tracking-tighter mb-2">Create <span className="text-gradient-gold">Account</span></h1>
                            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-primary/40">Register for the academy</p>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="space-y-3">
                                <Label htmlFor="name" className="text-[10px] font-black uppercase tracking-widest text-primary/60 ml-1">Full Name</Label>
                                <Input
                                    id="name"
                                    type="text"
                                    placeholder="Enter your name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    required
                                    className="h-14 rounded-2xl bg-white/50 border-white/80 focus:bg-white focus:ring-accent/20 transition-all duration-300 px-6 font-medium"
                                />
                            </div>
                            <div className="space-y-3">
                                <Label htmlFor="email" className="text-[10px] font-black uppercase tracking-widest text-primary/60 ml-1">Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="yourname@email.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    className="h-14 rounded-2xl bg-white/50 border-white/80 focus:bg-white focus:ring-accent/20 transition-all duration-300 px-6 font-medium"
                                />
                            </div>
                            <div className="space-y-3">
                                <Label htmlFor="password" className="text-[10px] font-black uppercase tracking-widest text-primary/60 ml-1">Password</Label>
                                <div className="relative">
                                    <Input
                                        id="password"
                                        type={showPassword ? "text" : "password"}
                                        placeholder="••••••••"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        className="h-14 rounded-2xl bg-white/50 border-white/80 focus:bg-white focus:ring-accent/20 transition-all duration-300 px-6 pr-12 font-medium"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-4 top-1/2 -translate-y-1/2 text-primary/40 hover:text-primary transition-colors focus:outline-none"
                                    >
                                        {showPassword ? (
                                            <EyeOff className="w-5 h-5" />
                                        ) : (
                                            <Eye className="w-5 h-5" />
                                        )}
                                    </button>
                                </div>
                            </div>

                            <Button type="submit" className="w-full h-16 btn-navy text-[10px] font-black uppercase tracking-widest shadow-premium mt-4" disabled={isLoading}>
                                {isLoading ? 'Creating account...' : 'Register'}
                            </Button>
                        </form>

                        <div className="mt-10 pt-8 border-t border-gray-100/50 text-center">
                            <span className="text-[10px] font-black uppercase tracking-widest text-primary/40">Already have an account? </span>
                            <Link to="/login" className="text-[10px] font-black uppercase tracking-widest text-accent hover:text-gold-dark transition-colors ml-2">
                                Login here
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default Register;
